import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import { api } from "@/lib/app";
import { UploadSimple, X, FileArchive, FileXls, FilePdf, FileDoc, FileCsv, CheckCircle, Warning, Gear, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACCEPT = ".xlsx,.xls,.csv,.zip,.rar,.pdf,.docx,.doc";

function iconForFile(name) {
  const n = name.toLowerCase();
  if (n.endsWith(".zip") || n.endsWith(".rar")) return FileArchive;
  if (n.endsWith(".xlsx") || n.endsWith(".xls")) return FileXls;
  if (n.endsWith(".csv")) return FileCsv;
  if (n.endsWith(".pdf")) return FilePdf;
  if (n.endsWith(".docx") || n.endsWith(".doc")) return FileDoc;
  return FileXls;
}

export default function SmartImportDialog({ onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [files, setFiles] = useState([]);          // [{file, kind}]
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const detectKind = (name) => {
    const n = name.toLowerCase();
    // PLAN markers — Slovak retail exports strip diacritics replacing 'á' with '_'
    // → 'plán' becomes 'pl_n', so detect that pattern explicitly.
    if (/(?:^|[_\-\s.])pl_n(?:[_\-\s.]|$)/.test(n)) return "plan";
    if (/(?:^|[_\-\s.])(plan|plán|pln)(?:[_\-\s.]|$)/.test(n)) return "plan";
    // REALITA markers
    if (/realita|skuto[cč]|actual|hodinoveho_vykonu|hodinov[eé]ho_v[yý]konu|export_hod/.test(n)) return "realita";
    return "auto";
  };

  const addFiles = (list) => {
    const arr = Array.from(list || []).map((f) => ({ file: f, kind: detectKind(f.name) }));
    setFiles((cur) => [...cur, ...arr]);
  };

  const setKind = (idx, kind) =>
    setFiles((cur) => cur.map((it, i) => (i === idx ? { ...it, kind } : it)));

  const removeFile = (idx) => setFiles((cur) => cur.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!files.length) return;
    setProcessing(true);
    setResults(null);
    try {
      const form = new FormData();
      for (const { file } of files) form.append("files", file);
      form.append("kinds", JSON.stringify(files.map((f) => f.kind)));
      const res = await api.post("/smart-import", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResults(res.data);
      mutate("/stores");
      toast.success(`Spracovaných ${res.data.stores.length} stredísk`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba pri spracovaní");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8" data-testid="smart-import-dialog">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)]">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-[var(--brand)]" />
            <h2 className="font-display text-xl font-semibold">Smart Import</h2>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]" data-testid="smart-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!results ? (
            <>
              <p className="text-sm text-[var(--text-secondary)]">
                Pretiahnite alebo vyberte súbory: <strong>xlsx, csv, zip, pdf, word</strong>.
                Aplikácia automaticky vytvorí stredisko (kód a názov sa rozpoznajú z názvu súboru, napr. <code className="bg-[var(--bg-page)] px-1 rounded">110041 - Nová Lesná-...csv</code>),
                naimportuje dáta, a z PDF/Word si AI vytiahne kontext.
              </p>
              <div className="card-flat bg-[#F4F7F5] border-l-4 border-[var(--brand)] p-3 text-xs space-y-1.5">
                <p className="font-semibold text-[var(--text-primary)]">📦 Čo môžete nahrať naraz (všetko v jednej dávke):</p>
                <ul className="list-disc list-inside space-y-1 pl-1 text-[var(--text-secondary)]">
                  <li><strong>Realita</strong> — hodinový výkon (<code>...Export_hodinoveho_vykonu.xlsx</code>) → ukladá sa ako hodinové záznamy.</li>
                  <li><strong>Plán</strong> — hodinový plán strediska (<code>..._pl_n.xlsx</code> / <code>_plan_</code> / <code>plán</code> v názve) → ukladá sa do plánov a používa sa v sekcii „Simulácia&rdquo;.</li>
                  <li><strong>Poznámky</strong> — len ak súbor obsahuje v názve <code>poznamky</code> alebo <code>poznámky</code>; iné PDF/Word sa použijú len na AI metadáta a do poznámok nejdú.</li>
                  <li><strong>Balíky</strong> — môžete nahrať aj <code>.zip</code> alebo <code>.rar</code> a všetko vnútri sa spracuje rovnako.</li>
                </ul>
                <p className="pt-1 text-[var(--text-muted)]">Aplikácia rozpozná plán vs realitu z názvu/obsahu súboru. Ak by sa pomýlila, kliknite v zozname nižšie na rozbaľovacie menu (Auto / Plán / Realita) a vynúťte správny typ.</p>
              </div>

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  addFiles(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-10 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
                  drag ? "border-[var(--brand)] bg-[var(--success-bg)]" : "border-[var(--border-default)] bg-[var(--bg-page)] hover:bg-[var(--bg-surface-hover)]"
                }`}
                data-testid="smart-drop"
              >
                <UploadSimple size={36} weight="duotone" className="text-[var(--brand)] mb-3" />
                <p className="font-medium">Pretiahnite súbory sem alebo kliknite pre výber</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">.xlsx · .xls · .csv · .zip · .rar · .pdf · .docx</p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="hidden"
                  data-testid="smart-file-input"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>

              {files.length > 0 && (
                <div className="card-flat">
                  <div className="px-4 py-2 border-b border-[var(--border-default)] flex items-center justify-between">
                    <p className="label-eyebrow">Pripravené ({files.length}) — pre každý súbor vyberte typ</p>
                    <button onClick={() => setFiles([])} className="text-xs text-[var(--critical)]" data-testid="smart-clear">
                      Vyčistiť všetko
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto scroll-area">
                    {files.map((it, i) => {
                      const f = it.file;
                      const Icon = iconForFile(f.name);
                      const isDoc = /\.(pdf|docx?|doc)$/i.test(f.name);
                      const isArchive = /\.(zip|rar)$/i.test(f.name);
                      const kindLabel = it.kind === "plan" ? "PLÁN" : it.kind === "realita" ? "REALITA" : "Auto";
                      const kindColor = it.kind === "plan" ? "text-[var(--brand)]" : it.kind === "realita" ? "text-[var(--success)]" : "text-[var(--text-muted)]";
                      return (
                        <div key={i} className="px-4 py-2 flex items-center gap-3 border-b border-[var(--border-default)] last:border-0">
                          <Icon size={18} className="text-[var(--brand)] shrink-0" weight="duotone" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate" title={f.name}>{f.name}</p>
                            <p className="text-xs text-[var(--text-muted)] tabular">
                              {(f.size / 1024).toFixed(1)} KB
                              {it.kind !== "auto" && (
                                <span className={`ml-2 font-medium ${kindColor}`}>· detekované: {kindLabel}</span>
                              )}
                            </p>
                          </div>
                          {!isDoc && !isArchive && (
                            <select
                              value={it.kind}
                              onChange={(e) => setKind(i, e.target.value)}
                              className="text-xs px-2 py-1 rounded border border-[var(--border-default)] bg-white"
                              data-testid={`smart-kind-${i}`}
                              title="Auto = detekcia z názvu a obsahu; Plán/Realita = vynútiť typ"
                            >
                              <option value="auto">Auto</option>
                              <option value="plan">📋 Plán</option>
                              <option value="realita">📊 Realita</option>
                            </select>
                          )}
                          <button onClick={() => removeFile(i)} className="text-[var(--text-muted)] hover:text-[var(--critical)] p-1">
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border-default)]">
                <button onClick={onClose} className="btn-ghost" data-testid="smart-cancel">Zrušiť</button>
                <button
                  onClick={submit}
                  disabled={processing || files.length === 0}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="smart-submit"
                >
                  <Sparkle size={14} weight="fill" /> {processing ? "Spracovávam…" : "Spracovať a vytvoriť"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3">
                {results.stores.map((s) => (
                  <div key={s.store_id} className="card-flat p-4" data-testid={`smart-result-${s.store_code}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="label-eyebrow mb-1">{s.created ? "Vytvorené" : "Aktualizované"}</p>
                        <h3 className="font-display text-lg font-semibold">
                          {s.store_code} · {s.store_name}
                        </h3>
                        <div className="text-sm text-[var(--text-secondary)] mt-2 space-y-0.5">
                          {s.imported_records > 0 && (
                            <p className="inline-flex items-center gap-1.5">
                              <CheckCircle size={14} weight="fill" className="text-[var(--success)]" />
                              <strong className="tabular">{s.imported_records}</strong> záznamov reality
                              {s.date_range && <span className="tabular text-[var(--text-muted)]"> · {s.date_range.from} → {s.date_range.to} ({s.date_range.days} dní)</span>}
                            </p>
                          )}
                          {s.imported_plans > 0 && (
                            <p className="inline-flex items-center gap-1.5">
                              <CheckCircle size={14} weight="fill" className="text-[var(--brand)]" />
                              <strong className="tabular">{s.imported_plans}</strong> záznamov plánu
                              {s.plan_range && <span className="tabular text-[var(--text-muted)]"> · {s.plan_range.from} → {s.plan_range.to} ({s.plan_range.days} dní)</span>}
                            </p>
                          )}
                          {s.notes_added > 0 && (
                            <p className="inline-flex items-center gap-1.5">
                              <CheckCircle size={14} weight="fill" className="text-[var(--success)]" />
                              <strong>{s.notes_added}</strong> {s.notes_added === 1 ? "poznámka" : "poznámky"}
                            </p>
                          )}
                          {s.ai_metadata_extracted && (
                            <p className="text-xs text-[var(--brand)]">🤖 AI vytiahla metadáta z PDF/Word</p>
                          )}
                          {s.warnings.map((w, i) => (
                            <p key={i} className="inline-flex items-start gap-1.5 text-[var(--warning)] text-xs">
                              <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                              {w}
                            </p>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          onClose();
                          navigate(`/strediska/${s.store_id}?editConfig=1`);
                        }}
                        className="btn-primary text-xs inline-flex items-center gap-1.5 shrink-0"
                        data-testid={`smart-open-${s.store_code}`}
                      >
                        <Gear size={14} /> Otvoriť a doladiť
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-2 border-t border-[var(--border-default)]">
                <button
                  onClick={() => {
                    setResults(null);
                    setFiles([]);
                  }}
                  className="btn-ghost"
                  data-testid="smart-more"
                >
                  Pridať ďalšie súbory
                </button>
                <button onClick={onClose} className="btn-primary ml-2" data-testid="smart-done">
                  Hotovo
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
